import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

export const FAQ: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('metadata.faq.title')}</title>
        <meta name="description" content={t('metadata.faq.description')} />
        <meta name="keywords" content="IPTV FAQ, streaming help, M3U8 troubleshooting, HLS player help, video streaming support, playlist management, account features" />
        <link rel="canonical" href="https://iptvplayer.stream/faq" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          Frequently Asked Questions
        </h1>
        
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Video Playback and Streaming
            </h2>
            
            <div className="space-y-6">
              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Why is my video not playing?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Ensure the video URL is correct and the video format is supported (HLS/M3U8). Check your internet connection and verify that the stream source is accessible.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Why is there no sound?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Check your computer's sound settings and ensure the video player is not muted. Click the volume icon in the player controls to adjust the volume level.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Why is the video buffering?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Buffering can occur due to slow internet connection or server issues. Try lowering the video quality if available, or check your network connection speed.
                </p>
              </article>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Quality and Performance
            </h2>
            
            <div className="space-y-6">
              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Why is the video quality poor?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Video quality depends on both your internet connection and the source stream quality. Ensure you have a stable internet connection and that the source provides high-quality streaming.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  How do I enable subtitles?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  If the video has subtitles available, you can enable them through the video player's settings. Click the subtitles icon in the player controls to select your preferred language.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Can I use Picture-in-Picture mode?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Yes, our player supports Picture-in-Picture mode. Click the PiP icon in the player controls to enable this feature. This allows you to watch content while browsing other tabs or applications.
                </p>
              </article>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Playlist Management
            </h2>
            
            <div className="space-y-6">
              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  How do I add channels to my playlist?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  You can add channels in two ways: import an M3U/M3U8 playlist file or add individual channels manually. Use the "Add" tab in the sidebar to access both options.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Can I organize my channels into groups?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Yes, when adding channels manually, you can specify a group name. Channels will be automatically organized under their respective groups in the playlist sidebar.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  How do I manage my favorites?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Click the star icon next to any channel to add it to your favorites. Favorite channels appear at the top of your playlist for quick access.
                </p>
              </article>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Account Features
            </h2>
            
            <div className="space-y-6">
              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Why should I create an account?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Creating an account allows you to sync your playlists across devices, save your favorites, and restore your channels if you switch browsers or devices.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Is my playlist data secure?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Yes, all your playlist data is securely stored and encrypted. We use industry-standard security practices to protect your information.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Can I export my playlist?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Yes, you can export your playlist as an M3U file. This allows you to backup your channels or use them in other compatible players.
                </p>
              </article>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              General Usage
            </h2>
            
            <div className="space-y-6">
              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Does IPTVPlayer work on mobile devices?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Yes, our web player is fully responsive and works on mobile devices. You can access your playlists and watch content on any device with a modern web browser.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Can I use keyboard shortcuts?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Yes, we support common keyboard shortcuts: Space for play/pause, F for fullscreen, M for mute, and arrow keys for volume and seeking.
                </p>
              </article>

              <article className="space-y-2">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  Is there a dark mode available?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Yes, you can toggle between light and dark mode using the theme switch in the top navigation bar. Your preference will be saved for future visits.
                </p>
              </article>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Additional Support
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400">
              If you're still experiencing issues or have questions not covered here, please visit our{' '}
              <a href="/contact" className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300">
                Contact page
              </a>{' '}
              for support.
            </p>
          </section>
        </div>
      </div>
    </>
  );
}; 